<script setup>
import SlideOver from '@/components/SlideOver.vue';
import { useModal } from '@/composables/useModal.js';
import { useForm } from '@inertiajs/vue3';
import FormInput from '@/components/Forms/FormInput.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormError from '@/components/Forms/FormError.vue';
import FormTextarea from '@/components/Forms/FormTextarea.vue';
import InertiaInlineSquareDeleteButton from '@/components/InertiaInlineSquareDeleteButton.vue';
import AlertMessage from '@/components/AlertMessage.vue';
import LinkBatchInlineForm from '@/components/pageSpecific/LinkBatchInlineForm.vue';
import BadgePill from '@/components/BadgePill.vue';
import { computed, ref } from 'vue';
import deburr from 'lodash/deburr.js';

const props = defineProps({
    tag: {},
    tags: {
        type: Array,
        default: () => [],
    },
});

const form = useForm({
    name: props.tag.name,
    description: props.tag.description,
    links: props.tag.form_links,
    expected_levels: props.tag.expectedLevels,
    tag_ids: props.tag.tag_ids,
});

const emit = defineEmits(['close']);
function submit() {
    form.patch(props.tag.links.update, {
        preserveScroll: true,
        onSuccess: () => {
            form.reset();
            emit('close');
            dismiss();
        },
    });
}

const selectedTags = computed(() => props.tags.filter((tag) => form.tag_ids.includes(tag.id)));
const deselectTag = (tag) => {
    form.tag_ids = form.tag_ids.filter((id) => id !== tag.id);
};
const selectTag = (tag) => {
    form.tag_ids.push(tag.id);
};
const toggleTag = (tag) => {
    if (form.tag_ids.includes(tag.id)) {
        deselectTag(tag);
    } else {
        selectTag(tag);
    }
};

const tagSearch = ref('');
const filteredTags = computed(() => {
    const search = deburr(tagSearch.value.toLowerCase());

    if (search.length < 2) {
        return [];
    }

    return props.tags.filter((tag) => deburr(tag.name.toLowerCase()).includes(search));
});

const { isOpen, dismiss } = useModal(true);
</script>
<script>
export default {
    name: 'EditTagPanel',
};
</script>

<template>
    <SlideOver
        size="xxl"
        :show="isOpen"
        @close="
            emit('close');
            dismiss();
        "
    >
        <form action="" @submit.prevent="submit" id="edit_tag_form">
            <div>
                <div class="mt-1">
                    <FormLabel :value="$t('messages.name')" for="name" />
                    <FormInput
                        id="name"
                        v-model="form.name"
                        name="name"
                        type="text"
                        max="60"
                        class="mt-1 w-full"
                        :placeholder="$tc('messages.content_blocks', 1)"
                    />

                    <FormError :value="form.errors.name" class="mt-2" />
                </div>
            </div>
            <div>
                <div class="mt-1">
                    <FormLabel :value="$t('messages.description')" for="description" />
                    <FormTextarea
                        rows="3"
                        id="description"
                        v-model="form.description"
                        :placeholder="$t('messages.(optional)')"
                        name="description"
                        type="text"
                        class="mt-1 w-full"
                    />
                    <FormError :value="form.errors.description" class="mt-2" />
                </div>
            </div>

            <div class="mt-3">
                <LinkBatchInlineForm :errors="form.errors" v-model="form.links" />
            </div>

            <div class="mt-3">
                <div>
                    {{ $tc('messages.expected_levels', 2) }}
                </div>
                <div v-for="(gradeLevel, index) in form.expected_levels" :key="gradeLevel.grade_level_id" class="mt-3">
                    <FormLabel :value="gradeLevel.name" />
                    <FormInput
                        v-model="gradeLevel.level"
                        type="number"
                        step="1"
                        min="1"
                        max="5"
                        :placeholder="`1-5 ${$t('messages.(optional)')}`"
                        class="w-full"
                    />
                    <FormError :value="form.errors[`expected_levels.${index}.level`]" />
                </div>
            </div>

            <div class="mt-3">
                <div>
                    {{ $tc('messages.prerequisites', 2) }}
                </div>
                <div class="flex flex-wrap items-center gap-2" v-auto-animate>
                    <button
                        v-for="selectedTag in selectedTags"
                        :key="selectedTag.id"
                        @click="deselectTag(selectedTag)"
                        type="button"
                    >
                        <BadgePill>
                            {{ selectedTag.name }}
                        </BadgePill>
                    </button>
                </div>
                <div>
                    <FormLabel :value="$t('messages.search')" />
                    <FormInput v-model="tagSearch" type="text" class="w-full" />
                    <div class="flex flex-wrap items-center gap-2 mt-3" v-auto-animate>
                        <button
                            v-for="selectedTag in filteredTags"
                            :key="selectedTag.id"
                            @click="toggleTag(selectedTag)"
                            type="button"
                        >
                            <BadgePill :color="form.tag_ids.includes(selectedTag.id) ? 'indigo' : 'gray'">
                                {{ selectedTag.name }}
                            </BadgePill>
                        </button>
                    </div>
                </div>
            </div>
        </form>

        <div class="py-3 w-full border-t border-gray-300" />
        <div>
            <AlertMessage class="mt-5 inline-block" type="info">
                <template v-slot:title>
                    {{ $t('messages.information') }}
                </template>
                <template v-slot:body> {{ $t('messages.deleting_tag_keeps_questions_explanation') }} </template>
            </AlertMessage>
        </div>
        <div class="mt-3">
            <InertiaInlineSquareDeleteButton
                :href="tag.links.delete"
                @deleted="
                    emit('close');
                    dismiss();
                "
            />
        </div>

        <template v-slot:bottom>
            <div class="flex justify-end">
                <SubmitFormButton
                    :button-text="$t('messages.save')"
                    :processing="form.processing"
                    form="edit_tag_form"
                />
            </div>
        </template>
    </SlideOver>
</template>
